import { useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '@/shared/api/httpClient';
import { classifyApiError } from '@/shared/api/classifyApiError';
import { employeeKeys } from '@/modules/employees/application/employeeKeys';
import { enrollV2EmbeddingUseCase, type EnrollV2EmbeddingInput } from '@/modules/attendance/application/use-cases/EnrollV2EmbeddingUseCase';

export const useEnrollPinMutation = () => {
  const qc = useQueryClient();
  return useMutation<number, Error, { idEntity: number; pin: string }>({
    mutationFn: async ({ idEntity, pin }) => {
      try {
        const res = await httpClient.post<number>(
          `/v1/clock/app/entity/${idEntity}/enroll/pin`,
          { pin },
        );
        return res.data;
      } catch (err) {
        throw classifyApiError(err);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: employeeKeys.all() });
    },
  });
};

export const useEnrollCardMutation = () => {
  const qc = useQueryClient();
  return useMutation<number, Error, { idEntity: number; decimal: number }>({
    mutationFn: async ({ idEntity, decimal }) => {
      try {
        // NOTE: backend endpoint path segment is `/person/` but the value
        // it receives is the employee's `id_entity` (not `id_person`).
        // This is a backend API quirk we cannot rename from the client.
        const res = await httpClient.post<number>(
          `/v1/clock/app/person/${idEntity}/enroll/card`,
          { decimal },
        );
        return res.data;
      } catch (err) {
        throw classifyApiError(err);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: employeeKeys.all() });
    },
  });
};

/**
 * Mutation: enroll facial v2 embedding for an employee.
 * 
 * networkMode: 'always' — enrollment works offline (saves to Dexie, syncs later).
 * Wraps enrollV2EmbeddingUseCase and invalidates employee queries on success.
 * 
 * The composite repo's offline-first architecture ensures that when the detail
 * page queries after invalidation, it reads the fresh embedding + photo from Dexie.
 */
export const useEnrollFaceMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, EnrollV2EmbeddingInput>({
    networkMode: 'always',
    mutationFn: async (input) => {
      await enrollV2EmbeddingUseCase(input);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: employeeKeys.all() });
    },
  });
};

